import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft, Newspaper, ExternalLink } from "lucide-react";

export default function PublicNewsListPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [skip, setSkip] = useState(0);
  const LIMIT = 20;

  const load = async (nextSkip = 0) => {
    setLoading(true);
    try {
      const { data } = await api.get("/public/news/all", { params: { limit: LIMIT, skip: nextSkip } });
      setItems((prev) => (nextSkip === 0 ? (data.items || []) : [...prev, ...(data.items || [])]));
      setTotal(data.total || 0);
      setSkip(nextSkip);
    } catch (_e) {
      /* silent */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(0); }, []);

  const hasMore = items.length < total;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-news-list-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-4 md:py-6 flex items-center gap-3">
          <Link to="/" className="text-slate-600 hover:text-slate-900 inline-flex items-center gap-1 text-sm" data-testid="btn-back-home">
            <ArrowLeft className="h-4 w-4" /> Kembali
          </Link>
          <div className="h-6 w-px bg-slate-200" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-semibold flex items-center gap-1.5">
              <Newspaper className="h-3 w-3" /> Info Terbaru
            </div>
            <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight mt-0.5">
              Semua Berita &amp; Pengumuman
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-8 py-6 md:py-8">
        {loading && items.length === 0 && (
          <div className="text-center text-slate-500 py-16">Memuat...</div>
        )}
        {!loading && items.length === 0 && (
          <div className="text-center text-slate-500 py-16 border border-dashed border-slate-300 rounded-md bg-white">
            Belum ada berita.
          </div>
        )}

        <ul className="grid grid-cols-1 gap-4 max-w-2xl mx-auto" data-testid="public-news-full-list">
          {items.map((n) => {
            const isInternal = n.mode === "internal" && n.slug;
            const target = isInternal ? `/${n.slug}` : n.url;
            const linkProps = isInternal
              ? { to: target }
              : { href: target, target: "_blank", rel: "noopener noreferrer" };
            const Tag = isInternal ? Link : "a";
            return (
              <li key={n.id}>
                <Tag
                  {...linkProps}
                  className="block h-full bg-white rounded-lg border border-slate-200 hover:border-slate-900 hover:shadow-sm transition overflow-hidden group"
                  data-testid={`public-news-item-${n.id}`}
                >
                  {n.cover_url ? (
                    <div className="aspect-[16/9] w-full bg-slate-100 overflow-hidden flex items-center justify-center">
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${n.cover_url}`}
                        alt={n.title}
                        className="max-w-full max-h-full w-auto h-auto object-contain group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                  ) : (
                    <div className="aspect-[16/9] w-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                      <Newspaper className="h-10 w-10 text-slate-300" />
                    </div>
                  )}
                  <div className="p-4">
                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                      {isInternal ? "Artikel" : <>Link Eksternal <ExternalLink className="h-2.5 w-2.5" /></>}
                      {(n.display_date || n.created_at) && (
                        <span className="ml-auto normal-case tracking-normal text-slate-400 font-normal">
                          {new Date(n.display_date || n.created_at).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" })}
                        </span>
                      )}
                    </div>
                    <h3 className="font-display font-bold text-base md:text-lg tracking-tight text-slate-900 mt-1 line-clamp-2">
                      {n.title}
                    </h3>
                    {n.excerpt && (
                      <p className="text-xs text-slate-600 mt-1.5 line-clamp-2">{n.excerpt}</p>
                    )}
                  </div>
                </Tag>
              </li>
            );
          })}
        </ul>

        {hasMore && (
          <div className="text-center mt-8">
            <button
              onClick={() => load(skip + LIMIT)}
              disabled={loading}
              className="px-5 py-2 rounded-md bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold disabled:opacity-50"
              data-testid="btn-load-more"
            >
              {loading ? "Memuat..." : `Muat lebih banyak (${total - items.length} sisa)`}
            </button>
          </div>
        )}

        {!hasMore && items.length > 0 && (
          <div className="text-center text-xs text-slate-400 mt-8">
            Menampilkan semua {total} berita.
          </div>
        )}
      </main>
    </div>
  );
}
